/**
 * Application Initialization Utilities
 * Handles app startup, configuration validation, and initialization sequence
 */

import { env, validateEnvironment, getEnvironmentInfo } from './env';
import { errorLogger } from './errorHandling';
import { APP_INFO, API_CONFIG, FEATURE_FLAGS } from '../constants';
import { createErrorReport } from './errorHandling';
import { createNavigationLogger, ScopedLoggerType } from './logger';

// ============================================================================
// React Import for Hook
// ============================================================================

import React from 'react';

// ============================================================================
// Initialization States
// ============================================================================

export interface InitState {
  isInitialized: boolean;
  isLoading: boolean;
  error?: Error | null;
  initStartTime: number;
  initEndTime?: number;
}

export interface InitResult {
  success: boolean;
  error?: Error;
  warnings: string[];
  duration: number;
}

// ============================================================================
// Initialization Manager Class
// ============================================================================

export class AppInitializer {
  private static instance: AppInitializer;
  private initState: InitState;
  private initPromise: Promise<InitResult> | null = null;
  private readonly logger: ScopedLoggerType = createNavigationLogger();

  private constructor() {
    this.initState = {
      isInitialized: false,
      isLoading: false,
      error: null,
      initStartTime: 0,
    };
  }

  static getInstance(): AppInitializer {
    if (!AppInitializer.instance) {
      AppInitializer.instance = new AppInitializer();
    }
    return AppInitializer.instance;
  }

  // Public API
  async initialize(): Promise<InitResult> {
    // Prevent multiple initialization attempts
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.performInitialization();
    return this.initPromise;
  }

  getInitState(): InitState {
    return { ...this.initState };
  }

  isInitialized(): boolean {
    return this.initState.isInitialized;
  }

  isInitializing(): boolean {
    return this.initState.isLoading;
  }

  getLogger(): ScopedLoggerType {
    return this.logger;
  }

  private async performInitialization(): Promise<InitResult> {
    const startTime = Date.now();
    this.initState = {
      ...this.initState,
      isLoading: true,
      initStartTime: startTime,
      error: null,
    };

    const warnings: string[] = [];

    try {
      // Phase 1: Environment Validation
      const envValidation = await this.validateEnvironment();
      warnings.push(...envValidation.warnings);

      // Phase 2: Core Services Initialization
      await this.initializeCoreServices();

      // Phase 3: Feature Detection
      await this.detectFeatures();

      // Phase 4: Performance Monitoring
      this.initializePerformanceMonitoring();

      // Phase 5: Development Tools
      if (env.isDevelopment) {
        this.initializeDevTools();
      }

      // Success
      const endTime = Date.now();
      this.initState = {
        ...this.initState,
        isInitialized: true,
        isLoading: false,
        initEndTime: endTime,
      };

      const result: InitResult = {
        success: true,
        warnings,
        duration: endTime - startTime,
      };

      if (env.shouldLog('info')) {
        this.logger.info(
          'App initialization completed successfully',
          undefined,
          {
            duration: result.duration,
            warningCount: warnings.length,
            environment: env.nodeEnv,
          },
        );
      }

      return result;
    } catch (error) {
      const endTime = Date.now();
      const appError =
        error instanceof Error ? error : new Error(String(error));

      this.initState = {
        ...this.initState,
        isLoading: false,
        error: appError,
        initEndTime: endTime,
      };

      // Log initialization error
      const errorReport = createErrorReport(appError, undefined, {
        phase: 'initialization',
        duration: endTime - startTime,
      });
      errorLogger.logError(errorReport);

      this.logger.error('App initialization failed', appError);

      const result: InitResult = {
        success: false,
        error: appError,
        warnings,
        duration: endTime - startTime,
      };

      return result;
    }
  }

  private async validateEnvironment(): Promise<{ warnings: string[] }> {
    const warnings: string[] = [];

    try {
      // Validate environment configuration
      const validation = validateEnvironment();
      if (!validation.isValid) {
        warnings.push(
          ...validation.errors.map((error) => `Environment: ${error}`),
        );
      }

      // Check for required environment variables
      const requiredEnvVars = ['REACT_APP_API_BASE_URL'];

      for (const envVar of requiredEnvVars) {
        if (!process.env[envVar]) {
          warnings.push(`Missing environment variable: ${envVar}`);
        }
      }

      // Validate API connectivity (optional check)
      if (API_CONFIG.BASE_URL && env.isDevelopment) {
        try {
          // Use apiService for consistent endpoint handling
          const { apiService } = await import('../services/api');
          await apiService.health();
        } catch (error) {
          warnings.push('Cannot connect to API - check network connectivity');
        }
      }
    } catch (error) {
      warnings.push(
        `Environment validation failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    if (warnings.length > 0) {
      this.logger.warn('Environment validation warnings', undefined, {
        warnings,
      });
    }

    return { warnings };
  }

  private async initializeCoreServices(): Promise<void> {
    if (env.shouldLog('info')) {
      this.logger.info('Initializing core services');
    }

    // Additional service initialization can be added here
    // For example: analytics, monitoring, feature flags, etc.

    // Simulate async initialization
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  private async detectFeatures(): Promise<void> {
    // Detect browser features and capabilities
    const features = {
      webgl: !!document.createElement('canvas').getContext('webgl'),
      localStorage: (() => {
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
          return true;
        } catch {
          return false;
        }
      })(),
      serviceWorker: 'serviceWorker' in navigator,
      webWorker: 'Worker' in window,
      fileReader: 'FileReader' in window,
      dragAndDrop: 'draggable' in document.createElement('div'),
    };

    if (env.shouldLog('debug')) {
      this.logger.debug('Feature detection completed', undefined, { features });
    }

    // Store feature detection results for use in components
    (window as any).__APP_FEATURES__ = features;
  }

  private initializePerformanceMonitoring(): void {
    // Initialize performance monitoring
    if ('performance' in window && 'mark' in performance) {
      performance.mark('app-initialized');

      if (env.shouldLog('debug')) {
        this.logger.debug('Performance monitoring initialized');
      }
    }
  }

  private initializeDevTools(): void {
    if (!env.enableDevTools) return;

    // Add development helpers to window object
    (window as any).__APP_DEV__ = {
      env: getEnvironmentInfo(),
      constants: APP_INFO,
      featureFlags: FEATURE_FLAGS,
      errorQueue: () => errorLogger.getErrorQueue(),
      clearErrors: () => errorLogger.clearErrorQueue(),
    };

    if (env.shouldLog('debug')) {
      this.logger.debug('Development tools initialized');
      this.logger.info('Access development helpers via window.__APP_DEV__');
    }
  }
}

// ============================================================================
// Global Initialization Instance
// ============================================================================

export const appInitializer = AppInitializer.getInstance();

// ============================================================================
// Initialization Hook for React Components
// ============================================================================

export function useAppInitialization() {
  const [initState, setInitState] = React.useState<InitState>(
    appInitializer.getInitState(),
  );
  const logger = React.useMemo(() => appInitializer.getLogger(), []);

  React.useEffect(() => {
    // If not initialized and not initializing, start initialization
    if (!initState.isInitialized && !initState.isLoading) {
      appInitializer
        .initialize()
        .then((result) => {
          setInitState(appInitializer.getInitState());

          // Handle initialization result
          if (!result.success && result.error) {
            logger.error('App initialization failed', result.error);
          } else if (result.warnings.length > 0) {
            logger.warn('App initialization warnings', undefined, {
              warnings: result.warnings,
            });
          }
        })
        .catch((error) => {
          logger.error('App initialization error', error);
          setInitState({
            isInitialized: true,
            isLoading: false,
            error,
            initStartTime: Date.now(),
            initEndTime: Date.now(),
          });
        });
    }
  }, [initState.isInitialized, initState.isLoading, logger]);

  return {
    isInitialized: initState.isInitialized,
    isLoading: initState.isLoading,
    error: initState.error,
    duration: initState.initEndTime
      ? initState.initEndTime - initState.initStartTime
      : 0,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if the app is ready for user interaction
 */
export function isAppReady(): boolean {
  return appInitializer.isInitialized();
}

/**
 * Get initialization status for debugging
 */
export function getInitStatus(): InitState {
  return appInitializer.getInitState();
}

/**
 * Force re-initialization (useful for testing or recovery)
 */
export async function reinitializeApp(): Promise<InitResult> {
  appInitializer.getLogger().info('Re-initializing app');
  return appInitializer.initialize();
}
